<template>
	<div data-tour-scope="alerts">
		<v-main>
			<v-container fluid class="al-page">
				<div class="al-top">
					<div>
						<div class="al-title">Avvisi</div>
						<div class="al-muted">
							Clienti che hanno chiesto di essere avvisati quando un prodotto torna disponibile o quando il prezzo scende: chi aspetta, chi è stato avvisato e chi poi ha comprato.
						</div>
					</div>
					<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
					<v-spacer></v-spacer>
					<v-menu offset-y>
						<template v-slot:activator="{ on, attrs }">
							<v-btn text color="primary" v-bind="attrs" data-tour="export" v-on="on"><v-icon left>mdi-arrow-collapse-down</v-icon>Esporta</v-btn>
						</template>
						<v-list dense>
							<v-list-item link @click="esporta('csv')">
								<v-list-item-title class="size9">Esporta in CSV</v-list-item-title>
							</v-list-item>
							<v-list-item link @click="esporta('excel')">
								<v-list-item-title class="size9">Esporta in Excel</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</div>

				<v-tabs v-model="tabTipo" class="al-tabs mb-3" data-tour="types" background-color="transparent">
					<v-tab v-for="t in TIPI" :key="t.value"
						><v-icon small left>{{ t.icona }}</v-icon
						>{{ t.label }}</v-tab
					>
				</v-tabs>

				<AlertsKpi :summary="getSummary" :type="tipo" />

				<SectionCard :title="tipoCorrente.label" :icon="tipoCorrente.icona" :subtitle="total === 1 ? '1 richiesta' : total + ' richieste'" class="mt-4">
					<div class="al-filters" data-tour="filters">
						<v-text-field
							v-model="searchfield"
							label="Cerca email, prodotto, SKU o EAN"
							outlined
							dense
							hide-details
							prepend-inner-icon="mdi-magnify"
							clearable
							class="al-search"
							@keyup.enter="getSearch()"
							@click:clear="pulisciRicerca"
						/>
						<v-text-field v-model="settings.filters.from" type="date" label="Dal" outlined dense hide-details clearable class="al-date" />
						<v-text-field v-model="settings.filters.to" type="date" label="Al" outlined dense hide-details clearable class="al-date" />
						<div class="al-states">
							<v-chip small :outlined="settings.filters.status !== ''" :color="settings.filters.status === '' ? 'primary' : undefined" @click="settings.filters.status = ''">Tutti</v-chip>
							<v-chip
								v-for="(s, codice) in STATI"
								:key="codice"
								small
								:outlined="settings.filters.status !== codice"
								:color="settings.filters.status === codice ? s.colore : undefined"
								:text-color="settings.filters.status === codice ? 'white' : undefined"
								@click="settings.filters.status = codice"
							>
								<v-icon x-small left>{{ s.icona }}</v-icon
								>{{ s.label }}
							</v-chip>
						</div>
					</div>

					<div v-if="!getItems.length" class="al-empty">
						<v-icon large color="grey lighten-1">mdi-bell-off-outline</v-icon>
						<div>Nessuna richiesta{{ filtriAttivi ? ' con questi filtri' : '' }}.</div>
						<div class="al-muted">{{ tipoCorrente.vuoto }}</div>
					</div>

					<v-data-table
						v-else
						:headers="headers"
						:items="getItems"
						:options.sync="settings.options"
						:server-items-length="total"
						must-sort
						class="elevation-0"
						:footer-props="{ 'items-per-page-options': listSize }"
						item-key="_id"
						data-tour="table"
						:hide-default-header="mobileCards"
						:class="{ 'table-cards': mobileCards }"
					>
						<template v-slot:body="{ items }">
							<!-- telefono: ogni richiesta diventa una card -->
							<tbody v-if="mobileCards" class="al-cards">
								<tr v-for="item in items" :key="item._id">
									<td :colspan="headers.length" class="px-0 py-1">
										<key-value-card :fields="campiCard(item)" :image="item.item.image || ''">
											<!-- titolo intero: dice quale variante ha scelto il cliente -->
											<template #title>
												<span class="al-name">{{ item.item.title || 'Prodotto' }}</span>
											</template>
											<template #actions>
												<AlertRowMenu :item="item" @cancel="chiediAnnulla" @open-item="apriProdotto" @open-order="apriOrdine" />
											</template>
										</key-value-card>
									</td>
								</tr>
							</tbody>
							<tbody v-else>
								<tr v-for="item in items" :key="item._id">
									<td class="pl-1 pr-0" style="width: 44px">
										<AlertRowMenu :item="item" @cancel="chiediAnnulla" @open-item="apriProdotto" @open-order="apriOrdine" />
									</td>
									<td class="text-no-wrap">{{ item.created_at | moment('DD/MM/YYYY HH:mm') }}</td>
									<td style="width: 64px">
										<div class="al-thumb">
											<img v-if="item.item.image" :src="item.item.image" alt="" loading="lazy" />
											<v-icon v-else small color="grey lighten-1">mdi-image-off-outline</v-icon>
										</div>
									</td>
									<td class="text-left">
										<div class="font-weight-medium al-name">{{ item.item.title || '—' }}</div>
										<div class="al-muted">{{ item.item.sku || item.item.ean }}</div>
									</td>
									<td class="text-left">
										<div>{{ item.email }}</div>
										<v-chip v-if="item.newsletter" x-small label outlined color="green darken-1" title="Ha dato il consenso alla newsletter">
											<v-icon x-small left>mdi-email-newsletter</v-icon>Newsletter
										</v-chip>
									</td>
									<td class="text-uppercase">{{ item.lang || '—' }}</td>
									<td>
										<v-chip x-small label outlined :color="stato(item.status).colore">
											<v-icon x-small left>{{ stato(item.status).icona }}</v-icon
											>{{ stato(item.status).label }}
										</v-chip>
									</td>
									<template v-if="tipo === 'price'">
										<td class="text-no-wrap">{{ prezzo(item.price_seen) }}</td>
										<td class="text-no-wrap">
											<span v-if="item.price_sent != null" class="green--text text--darken-2 font-weight-medium">{{ prezzo(item.price_sent) }}</span>
											<span v-else class="al-muted">—</span>
										</td>
									</template>
									<td class="text-no-wrap">
										<span v-if="item.sent_at">{{ item.sent_at | moment('DD/MM/YYYY HH:mm') }}</span>
										<span v-else class="al-muted">—</span>
									</td>
									<td class="text-no-wrap">
										<template v-if="item.order">
											<div>{{ item.order.number || 'Ordine' }}</div>
											<div class="al-muted">{{ item.converted_at | moment('DD/MM/YYYY') }}</div>
										</template>
										<span v-else class="al-muted">—</span>
									</td>
								</tr>
							</tbody>
						</template>
					</v-data-table>
				</SectionCard>

				<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" multi-line :timeout="snackbar.timeout">
					{{ snackbar.text }}
					<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
				</v-snackbar>
			</v-container>
		</v-main>

		<v-dialog v-model="dialogAnnulla" max-width="340">
			<v-card>
				<v-card-title class="headline">Annullare la richiesta?</v-card-title>
				<v-card-text class="text-left">Il cliente non riceverà l’avviso quando il prodotto torna disponibile. La richiesta resta nello storico come annullata.</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn color="primary" text @click="dialogAnnulla = false"> Indietro </v-btn>
					<v-btn color="error" text @click="annulla"> Annulla richiesta </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import SectionCard from '@/components/core/SectionCard.vue';
import { listRouteState } from '@/components/core/list/listRouteState';
import { openWithReturn } from '@/components/core/pageReturn';
import AlertsKpi from '@/components/alerts/AlertsKpi.vue';
import AlertRowMenu from '@/components/alerts/AlertRowMenu.vue';
import { createAlertsTourSteps } from '@/components/alerts/alerts-tour';
import { STATI, TIPI, stato } from '@/components/alerts/formato';

const LIST_SIZE = [15, 25, 50, 100];

export default {
	name: 'AlertsList',
	// pagina, righe, ordinamento, ricerca, stato e periodo stanno nell'URL
	mixins: [listRouteState({ filters: { type: 'string', status: 'string', from: 'string', to: 'string' }, perPageOptions: LIST_SIZE })],
	setup() {
		return { tour: useTour(createAlertsTourSteps()) };
	},
	components: { TourOverlay, KeyValueCard, SectionCard, AlertsKpi, AlertRowMenu },
	data() {
		return {
			snackbar: { snackbar: false, color: '', timeout: 5000, text: '' },
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { type: 'available', status: '', from: '', to: '' },
				options: { sortBy: ['created_at'], sortDesc: [true], page: 1, itemsPerPage: 15 },
			}),
			listSize: LIST_SIZE,
			searchfield: '',
			total: 0,
			STATI,
			TIPI,
			dialogAnnulla: false,
			daAnnullare: null,
		};
	},
	mounted() {
		this.searchfield = this.settings.search || '';
		this.listLoad();
	},
	computed: {
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		tipo() {
			return this.settings.filters.type === 'price' ? 'price' : 'available';
		},
		tipoCorrente() {
			return TIPI.find(t => t.value === this.tipo);
		},
		// le schede sono il filtro "type" della lista: cambiarle riparte da pagina 1 e resta nell'URL
		tabTipo: {
			get() {
				return Math.max(
					0,
					TIPI.findIndex(t => t.value === this.tipo),
				);
			},
			set(i) {
				this.settings.filters.type = TIPI[i].value;
			},
		},
		headers() {
			const prezzi =
				this.tipo === 'price'
					? [
							{ text: 'Prezzo visto', value: 'price_seen' },
							{ text: 'Prezzo avvisato', value: 'price_sent' },
					  ]
					: [];
			return [
				{ text: '', value: '', sortable: false },
				{ text: 'Richiesta', value: 'created_at' },
				{ text: '', value: 'image', sortable: false },
				{ text: 'Prodotto', value: 'Item.Title' },
				{ text: 'Email', value: 'email' },
				{ text: 'Lingua', value: 'lang', sortable: false },
				{ text: 'Stato', value: 'status', sortable: false },
				...prezzi,
				{ text: 'Avvisato il', value: 'sent_at' },
				{ text: 'Ordine', value: 'converted_at' },
			];
		},
		filtriAttivi() {
			const f = this.settings.filters;
			return !!(f.status || f.from || f.to || this.settings.search);
		},
		...mapGetters({
			getItems: 'alerts/getItems',
			getSummary: 'alerts/getSummary',
		}),
	},
	methods: {
		...mapMutations({ setPreload: 'settings/setPreload', setLogout: 'profile/setLogout' }),
		...mapActions({
			fetchItems: 'alerts/asyncFetchData',
			fetchSummary: 'alerts/fetchSummary',
			cancel: 'alerts/cancel',
			destroyData: 'alerts/destroyData',
		}),
		stato,
		prezzo(v) {
			return v == null ? '—' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
		},
		campiCard(item) {
			const prezzi =
				this.tipo === 'price'
					? [
							{ label: 'Prezzo visto', value: this.prezzo(item.price_seen) },
							{ label: 'Prezzo avvisato', value: item.price_sent != null ? this.prezzo(item.price_sent) : '' },
					  ]
					: [];
			return [
				...prezzi,
				{ label: 'Stato', value: stato(item.status).label, strong: true },
				{ label: 'Email', value: `${item.email}${item.newsletter ? ' · newsletter' : ''}` },
				{ label: 'Richiesta', value: this.$moment(item.created_at).format('DD/MM/YYYY HH:mm') },
				{ label: 'Avvisato il', value: item.sent_at ? this.$moment(item.sent_at).format('DD/MM/YYYY HH:mm') : '' },
				{ label: 'Ordine', value: item.order ? item.order.number || 'sì' : '' },
			];
		},
		notifica(color, text) {
			this.snackbar = { snackbar: true, color, timeout: 6000, text };
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			const errors = res && res.data && res.data.error;
			this.notifica('error', _.isArray(errors) ? errors.map(e => e.msg).join(', ') : (res && res.data && res.data.message) || err.message);
		},
		// il mixin della lista riporta a pagina 1 e ricarica quando cambia la ricerca
		getSearch() {
			this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield : '';
		},
		pulisciRicerca() {
			this.searchfield = '';
			this.getSearch();
		},
		// lista e numeri insieme: i numeri dipendono dal periodo, che e' un filtro della lista
		getDataFromApi(preload = true) {
			if (preload) this.setPreload(true);
			const { from, to } = this.settings.filters;
			return Promise.all([this.fetchItems(this.settings), this.fetchSummary({ type: this.tipo, from, to }).catch(err => this.errore(err))])
				.then(([result]) => {
					this.total = result ? result.total : 0;
				})
				.catch(err => console.log(err))
				.finally(() => preload && this.setPreload(false));
		},
		esporta(format) {
			const { status, from, to } = this.settings.filters;
			this.exportAsync('alerts', format, { type: this.tipo, status, from, to, search: this.settings.search });
		},
		// la freccia della scheda riporta qui, con scheda, filtri e pagina dell'URL
		apriProdotto(item) {
			openWithReturn(this, { path: `/items/${item.item._id}` }, { label: 'Torna agli avvisi' });
		},
		apriOrdine(item) {
			openWithReturn(this, { path: `/orders/${item.order._id}` }, { label: 'Torna agli avvisi' });
		},
		chiediAnnulla(item) {
			this.daAnnullare = item;
			this.dialogAnnulla = true;
		},
		async annulla() {
			this.dialogAnnulla = false;
			const item = this.daAnnullare;
			if (!item) return;
			this.setPreload(true);
			try {
				await this.cancel({ item, type: this.tipo });
				this.notifica('success', 'Richiesta annullata');
				await this.getDataFromApi(false);
			} catch (err) {
				this.errore(err);
			} finally {
				this.daAnnullare = null;
				this.setPreload(false);
			}
		},
	},
	beforeDestroy() {
		this.tour.stop();
		this.destroyData();
	},
};
</script>

<style scoped>
.al-page {
	max-width: 1600px;
}
.al-top {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 12px;
	margin-bottom: 16px;
}
.al-title {
	font-size: 20px;
	font-weight: 600;
	color: #1f2d3d;
}
.al-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.al-page ::v-deep .section-card {
	height: auto;
}
.al-filters {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px 16px;
	margin-bottom: 8px;
}
.al-search {
	max-width: 300px;
}
.al-tabs {
	max-width: 520px;
}
.al-date {
	max-width: 170px;
}
.al-states {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.al-empty {
	text-align: center;
	padding: 32px 0;
	color: #5f6b7a;
}
.al-thumb {
	width: 48px;
	height: 48px;
	border-radius: 6px;
	background: #f2f4f8;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}
.al-thumb img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	background: #fff;
}
/* nome intero, anche su più righe: la parte finale è la variante scelta */
.al-name {
	max-width: 420px;
	white-space: normal;
	word-break: break-word;
}
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.al-cards,
.al-cards tr {
	display: block;
	width: 100%;
}
.al-cards tr,
.al-cards tr:hover {
	background: transparent !important;
}
.al-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
</style>
